/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { AfterViewInit, Component, OnDestroy, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TourMonthStatistics } from '@shared/models';
import * as TourActions from '@tours/store/tours.actions';
import { getCurrentDate, getMonthlyStatistics } from '@tours/store/tours.selectors';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CalendarComponent implements AfterViewInit, OnDestroy {
  @ViewChild('calendar', { static: true }) calendar: MatCalendar<Date>;
  private readonly today = new Date(new Date().setHours(0, 0, 0, 0));
  currentDate$: Observable<Date>;
  private statisticSubscription: Subscription;

  constructor(
    private store: Store<State>,
    private renderer: Renderer2,
    private translateService: TranslateService
  ) {
    this.currentDate$ = this.store.select(getCurrentDate);
  }

  ngAfterViewInit(): void {
    this.registerCalendarHeaderButtonEvents();
    this.store.dispatch(new TourActions.LoadMonthlyStatistics(this.calendar.activeDate));
    this.statisticSubscription = combineLatest(this.store.select(getMonthlyStatistics), this.translateService.get('core.availableDrivers'))
      .pipe(
        tap(([statistics, driversTranslation]) => {
          if (statistics) {
            this.updateStatistics(statistics, driversTranslation);
          }
        })
    ).subscribe();
  }

  ngOnDestroy(): void {
    if (this.statisticSubscription) {
      this.statisticSubscription.unsubscribe();
    }
  }

  setDate(date: Date): void {
    this.store.dispatch(new TourActions.SetCurrentDate(date));
  }

  setMonth(date: Date): void {
    this.setDate(date);
    this.store.dispatch(new TourActions.LoadMonthlyStatistics(date));
  }

  private registerCalendarHeaderButtonEvents(): void {
    document.querySelectorAll('mat-calendar mat-calendar-header button').forEach(button => {
      this.renderer.listen(
        button,
        'click',
        () => this.store.dispatch(new TourActions.LoadMonthlyStatistics(this.calendar.activeDate))
      );
    });
  }

  private updateStatistics(statistics: TourMonthStatistics, driversTranslation: string): void {
    // Material 21 renders calendar day cells as <button class="mat-calendar-body-cell">
    // (previously <td>), so match by class only.
    document.querySelectorAll('mat-calendar mat-month-view .mat-calendar-body-cell').forEach((calendarCell: HTMLElement) => {
      const day = Number(calendarCell.textContent.trim());
      const date = new Date(this.calendar.activeDate.getFullYear(), this.calendar.activeDate.getMonth(), day);

      this.renderer.removeClass(calendarCell, 'day-has-uncharged-tours');
      this.renderer.removeClass(calendarCell, 'day-has-available-drivers');

      if (undefined === statistics[day]) {
        return;
      }

      if (date < this.today && statistics[day].hasUnchargedTours) {
        this.renderer.addClass(calendarCell, 'day-has-uncharged-tours');
      } else if (date >= this.today && statistics[day].plannedToursCount > 0 && statistics[day].availableDriversCount > 0) {
        this.renderer.addClass(calendarCell, 'day-has-available-drivers');
      }

      calendarCell.title = `${statistics[day].availableDriversCount} ${driversTranslation}`;
    });
  }
}
